"use client"
import { HistoryCreateInput } from '@repo/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { services } from '../../../../lib/api/services';

// Query key factory for history
export const historyQueryKeys = {
  all: ['history'] as const,
  byInventaire: (inventaireId: number) => ['history', 'inventaire', inventaireId] as const,
  byId: (id: number) => ['history', id] as const,
};

/**
 * Hook to fetch history records for a specific inventaire
 */
export function useHistory(inventaireId: number | null) {

  return useQuery({
    queryKey: inventaireId ? historyQueryKeys.byInventaire(inventaireId) : ['history', 'empty'],
    queryFn: () => {
      if (!inventaireId) throw new Error('Inventaire ID is required');
      return services.history.getHistoryByInventaireId(inventaireId);
    },
    enabled: !!inventaireId,
    gcTime: 0,
    staleTime: 0,
    select: (data) => ({
      histories: data.data || [],
      total: data.total || 0,
      message: data.message
    })
  });
}

/**
 * Hook to fetch a single history record by ID
 */
export function useHistoryById(id: number | null) {
  return useQuery({
    queryKey: id ? historyQueryKeys.byId(id) : ['history', 'empty'],
    queryFn: () => {
      if (!id) throw new Error('History ID is required');
      return services.history.getHistoryById(id);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    select: (data) => data.data
  });
}

/**
 * Hook to create a new history record
 */
export function useCreateHistory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: HistoryCreateInput) => services.history.createHistory(data),
    onSuccess: (newHistory, variables) => {
      // Invalidate and refetch history lists
      queryClient.invalidateQueries({ queryKey: historyQueryKeys.all });

      // If we know the inventaire_id, specifically invalidate that query
      if (variables.inventaire_id) {
        queryClient.invalidateQueries({
          queryKey: historyQueryKeys.byInventaire(variables.inventaire_id)
        });
      }
    },
    onError: (error) => {
      console.error('Error creating history:', error);
    }
  });
}

/**
 * Hook to delete a history record
 */
export function useDeleteHistory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => services.history.deleteHistory(id),
    onSuccess: (_, deletedId) => {
      // Remove the specific history from cache
      queryClient.removeQueries({ queryKey: historyQueryKeys.byId(deletedId) });

      // Invalidate all history lists to refresh
      queryClient.invalidateQueries({ queryKey: historyQueryKeys.all });
    },
    onError: (error) => {
      console.error('Error deleting history:', error);
    }
  });
}
